import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, Zap, CreditCard, Calculator, Trophy, Gamepad2, Flame } from 'lucide-react';

const LINKS = [
  {
    to: '/student/daily-exercises',
    i18nKey: 'navigation.dailyExercises',
    Icon: Calendar,
    className:
      'border-cyan-300/90 bg-gradient-to-b from-cyan-50 to-white text-cyan-950 hover:border-cyan-400 hover:bg-cyan-100/90 shadow-[0_3px_0_0_rgba(6,182,212,0.2)]',
  },
  {
    to: '/student/flash-anzan',
    i18nKey: 'navigation.flashAnzan',
    Icon: Zap,
    className:
      'border-violet-300/90 bg-gradient-to-b from-violet-50 to-white text-violet-950 hover:border-violet-400 hover:bg-violet-100/90 shadow-[0_3px_0_0_rgba(139,92,246,0.2)]',
  },
  {
    to: '/student/flash-cards',
    i18nKey: 'navigation.flashCards',
    Icon: CreditCard,
    className:
      'border-rose-300/90 bg-gradient-to-b from-rose-50 to-white text-rose-950 hover:border-rose-400 hover:bg-rose-100/90 shadow-[0_3px_0_0_rgba(244,114,182,0.2)]',
  },
  {
    to: '/student/soroban',
    i18nKey: 'navigation.soroban',
    Icon: Calculator,
    className:
      'border-emerald-300/90 bg-gradient-to-b from-emerald-50 to-white text-emerald-950 hover:border-emerald-400 hover:bg-emerald-100/90 shadow-[0_3px_0_0_rgba(16,185,129,0.2)]',
  },
  {
    to: '/student/competitions',
    i18nKey: 'competitions.title',
    Icon: Trophy,
    className:
      'border-amber-300/90 bg-gradient-to-b from-amber-50 to-white text-amber-950 hover:border-amber-400 hover:bg-amber-100/90 shadow-[0_3px_0_0_rgba(245,158,11,0.22)]',
  },
  {
    to: '/student/games',
    i18nKey: 'navigation.games',
    Icon: Gamepad2,
    className:
      'border-fuchsia-300/90 bg-gradient-to-b from-fuchsia-50 to-white text-fuchsia-950 hover:border-fuchsia-400 hover:bg-fuchsia-100/90 shadow-[0_3px_0_0_rgba(217,70,239,0.2)]',
  },
  {
    to: '/student/challenges',
    i18nKey: 'navigation.challenges',
    Icon: Flame,
    className:
      'border-orange-300/90 bg-gradient-to-b from-orange-50 to-white text-orange-950 hover:border-orange-400 hover:bg-orange-100/90 shadow-[0_3px_0_0_rgba(249,115,22,0.22)]',
  },
];

/**
 * اختصارات للأنشطة — شبكة bento، حواف واضحة، حماس للتدريب.
 */
function StudentActivityQuickBar() {
  const { t } = useTranslation();

  return (
    <nav
      className="relative w-full min-w-0 overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/[0.06] bg-gradient-to-br from-white/95 via-sky-50/30 to-violet-50/40 px-3 py-3 shadow-[0_12px_40px_-28px_rgba(59,130,246,0.35)] ring-1 ring-white/80 backdrop-blur-sm sm:rounded-[1.5rem] sm:px-5 sm:py-4"
      aria-label={t('dashboard.student.activityQuickStartAria')}
    >
      <div
        className="pointer-events-none absolute -right-8 top-0 h-32 w-32 rounded-full bg-fuchsia-400/15 blur-2xl"
        aria-hidden
      />
      <div className="relative">
        <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
          <div>
            <p className="text-base font-black leading-tight text-slate-900 sm:text-lg">{t('dashboard.student.startNow')}</p>
            <p className="mt-1 text-[0.65rem] font-black uppercase tracking-[0.16em] text-violet-600 sm:text-xs">
              {t('dashboard.student.activityQuickStart')}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
          {LINKS.map(({ to, i18nKey, Icon, className }) => (
            <Link
              key={to}
              to={to}
              className={`group relative flex min-h-[3.25rem] min-w-0 flex-col items-center justify-center gap-1.5 rounded-2xl border-[3px] px-2 py-2.5 text-center text-[0.68rem] font-black leading-tight transition hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 sm:min-h-[3.5rem] sm:gap-2 sm:px-2.5 sm:py-3 sm:text-sm ${className}`}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/60 shadow-inner ring-1 ring-black/5 transition group-hover:scale-110 sm:h-10 sm:w-10">
                <Icon className="h-4 w-4 sm:h-[1.125rem] sm:w-[1.125rem]" strokeWidth={2.5} aria-hidden />
              </span>
              <span className="line-clamp-2 min-w-0 break-words">{t(i18nKey)}</span>
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
}

export default StudentActivityQuickBar;
